import { useEffect, useMemo, useState } from 'react';

import { useNavigate,  useParams } from "react-router-dom";

import { useAppContext } from '../../Context';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { setConsecutives, setConsecutive, setOrders, setOrder } from '../../slices/ordersSlice';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash, faPrint, faPlay } from '@fortawesome/free-solid-svg-icons';

import { usePermissions, printConsecutiveTable, printConsecutiveTableBatch } from '../../services';

import DataTable from '../../components/shared/DataTable';
import { Button } from '../../components/shared/FormElements';

import { AttributesType, ColumnsType, ConsecutiveType,  } from '../../types';
import { setLoading, setPopConfirmation } from '../../slices/miscSlice';

import { Grid } from "@mui/material";

import { constants } from '../../constants';

const ViewConsecutives: React.FC = () => {
    const { apiCall } = useAppContext();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const consecutives = useAppSelector((state : any) => state.orders.consecutives);
    const order = useAppSelector((state : any) => state.orders.order);

     const processStatusDecorator = {
        'En Proceso': 'secondary',
        'Finalizado': 'success',
        'Bloqueado': 'warning',
        'Por Iniciar': 'info',
        'Cancelado': 'error',
        'Sin proceso': 'warning',
    };

     const initialColumns = [
        {title: 'No.', field: 'consecutive_number', cell_class : 'text-center'},
        {title: 'Cliente', field: 'order.customer.customer_name'},
        {title: 'Línea', field: 'line.line_name'},
        {title: 'Fecha de Órden', field: 'order.order_date', cell_class : 'text-center'},
        {title: 'Fecha de Consecutivo', field: 'created_at', type: 'datetime', cell_class : 'text-center'},
        {title: 'Proceso', field: 'process_name_display'},
        {
            title: 'Estado Proceso',
            field: 'process_status_display',
            type: 'decorator',
            decorator: processStatusDecorator,
            cell_class: 'text-center'
        },
    ];
    const [columns, setColumns] = useState<ColumnsType[]>(initialColumns);

    const { id } = useParams();

    const {
        canView_orders,
        canAdd_orderconsecutive,
        canView_orderconsecutive,
        canDelete_orderconsecutive,
        canAdd_process,
        canValidate_orders,
        canView_orders_all,
        canView_orders_own,
    } = usePermissions([
        'view_orders',
        'add_orderconsecutive',
        'view_orderconsecutive',
        'delete_orderconsecutive',
        'add_process',
        'validate_orders',
        'view_orders_all',
        'view_orders_own'
    ]);

    const [enablePrint, setEnablePrint] = useState(false);

    const [selectedForPrint, setSelectedForPrint] = useState<Array<ConsecutiveType>>([]);

    useEffect(() => {

        if (!id) {
            navigate('/ordenes');
            return;
        }


        const getConsecutives = async () => {
            const response = await apiCall('GET', 'order/'+ id +'/consecutives/', null, '', 'Error al obtener los consecutivos');
            if (response) {
                dispatch(setConsecutives(response));
            }
        }

        const loadData = async () => {
            await getConsecutives();
            await refreshOrder();
        };

        loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , []);

    const consecutivesWithFlags = useMemo(() => {
        return consecutives.map((consecutive: ConsecutiveType) => {
            const is_sent_to_production = Boolean(consecutive.order_process_id || consecutive.order_process_status);
            return {
                ...consecutive,
                is_sent_to_production,
                process_name_display: consecutive.current_process_name || 'Sin proceso',
                process_status_display: consecutive.current_process_status || 'Sin proceso'
            };
        });
    }, [consecutives]);

    const hasUnsentConsecutives = useMemo(
        () =>
            consecutivesWithFlags.some(
                (consecutive: ConsecutiveType & { is_sent_to_production: boolean }) => !consecutive.is_sent_to_production
            ),
        [consecutivesWithFlags]
    );

    if (!canView_orderconsecutive) {
        navigate('/')
        return null;
    }

    const handleViewConsecutive = (consecutive : ConsecutiveType) => {
        dispatch(setConsecutive(consecutive));
        navigate(`/orden/${id}/consecutivo/${consecutive.id}`);
    }

    const handleDeleteConsecutive = ( consecutive : ConsecutiveType) => {
       dispatch(setPopConfirmation({show: true, message: '¿Está seguro que desea eliminar el consecutivo?', alertType: 'danger', okFunc: () => deleteConsecutive(consecutive.id) }));
    }
    const deleteConsecutive = async (consecutive_id : number) => {
        const response = await apiCall('DELETE', `order/${id}/consecutive/${consecutive_id}/`, null, '', 'Error al eliminar el consecutivo');
        if (response) {
            const ordersResponse = await apiCall('GET', 'orders/', null, '', 'Error al obtener las órdenes');

            if (ordersResponse) {
                dispatch(setOrders(ordersResponse));
            }

            dispatch(setConsecutives(consecutives.filter((c : ConsecutiveType) => c.id !== consecutive_id)));
        }
    }

    const handlePrintConsecutive= async (consecutive : ConsecutiveType) => {
        let boceto = null;
        const resp = await apiCall('GET', `order/${id}/consecutive/${consecutive.id}/boceto/`, null, '', 'Error al obtener el boceto');
        if (resp?.image){
            boceto = resp.image;
        }
        dispatch(setLoading(true));
        await printConsecutiveTable(consecutive, boceto);
        dispatch(setLoading(false));
    }

    const refreshOrder = async () => {
        if (!id) return;
        const response = await apiCall('GET', `order/${id}/`, null, '', 'Error al obtener la orden');
        if (response) {
            dispatch(setOrder(response));
        }
    }

    const refreshConsecutives = async () => {
        if (!id) return;
        const response = await apiCall('GET', `order/${id}/consecutives/`, null, '', 'Error al obtener los consecutivos');
        if (response) {
            dispatch(setConsecutives(response));
        }
    }

    const sendOrderToProduction = async (onlyOrder: boolean) => {
        if (!id) return;
        const response = await apiCall(
            'POST',
            `order/${id}/add_process/`,
            onlyOrder ? { only_order: true } : null,
            '',
            'Error al enviar la orden a producción'
        );
        if (response) {
            await refreshOrder();
            await refreshConsecutives();
        }
    }

    const sendOrderToValidation = async () => {
        if (!id) return;
        const response = await apiCall(
            'PUT',
            `order/${id}/validate/`,
            null,
            '',
            'Error al enviar la orden a validación'
        );
        if (response) {
            await refreshOrder();
        }
    }

    const handleSendOrderToProduction = (onlyOrder: boolean) => {
        dispatch(
            setPopConfirmation({
                show: true,
                message: onlyOrder
                    ? '¿Está seguro que desea enviar la orden a producción sin consecutivos?'
                    : '¿Está seguro que desea enviar los consecutivos a producción?',
                alertType: 'danger',
                okFunc: () => sendOrderToProduction(onlyOrder),
            })
        );
    }

    const handleSendOrderToValidation = () => {
        dispatch(
            setPopConfirmation({
                show: true,
                message: '¿Está seguro que desea enviar la orden a validación?',
                alertType: 'danger',
                okFunc: () => sendOrderToValidation(),
            })
        );
    }

    const sendConsecutiveToProduction = async (consecutive_id: number) => {
        if (!id) return;
        const response = await apiCall(
            'POST',
            `order/${id}/add_process/`,
            { consecutiveId: consecutive_id },
            '',
            'Error al enviar el consecutivo a producción'
        );
        if (response) {
            await refreshOrder();
            await refreshConsecutives();
        }
    }

    const handleSendConsecutiveToProduction = (consecutive: ConsecutiveType) => {
        dispatch(
            setPopConfirmation({
                show: true,
                message: '¿Está seguro que desea enviar este consecutivo a producción?',
                alertType: 'danger',
                okFunc: () => sendConsecutiveToProduction(consecutive.id),
            })
        );
    }

    const isLegacyLocked = Boolean(order?.legacy_production_flow && order?.order_process_id);
    const canSendConsecutiveToProduction =
        canAdd_process && !isLegacyLocked && order?.status !== constants.orderStatus.CREADA;

    const actions : Array<any> = [
        canView_orderconsecutive ?  {icon: <FontAwesomeIcon icon={faEye} />, tooltip: 'Ver', onClick: (rowData :ConsecutiveType) => handleViewConsecutive(rowData)} : null,
        canDelete_orderconsecutive ? {icon: <FontAwesomeIcon icon={faTrash} />, tooltip: 'Eliminar', onClick: ( rowData :any) => handleDeleteConsecutive(rowData)} : null,
        canView_orderconsecutive ? {icon: <FontAwesomeIcon icon={faPrint} />, tooltip: 'Imprimir', onClick: (rowData :any) => handlePrintConsecutive(rowData)} : null,
        canSendConsecutiveToProduction ? {icon: <FontAwesomeIcon icon={faPlay} />, tooltip: 'Enviar a Producción', onClick: (rowData :any) => handleSendConsecutiveToProduction(rowData), condition: { field: 'is_sent_to_production', value: false, operator: '===' }} : null,
    ];

    const filterDate : Array<AttributesType> = [
        {value: 'order.order_date', name: 'Fecha de Órden'},
        {value: 'created_at', name: 'Fecha de Consecutivo   '},
    ]

    const handlePrintSelecteAll = () => {
        setSelectedForPrint(consecutives);
    }

    const handlePrintSelecteNone = () => {
        setSelectedForPrint([]);
    }

    const handlePrintConsecutiveBatch = async () => {
        dispatch(setLoading(true));
        await printConsecutiveTableBatch(selectedForPrint);
        dispatch(setLoading(false));
    }

    const getBocetosForPrint = async () => {
        return new Promise((resolve, reject) => {
            const newConsecutives = consecutives.map(async (c : ConsecutiveType) => {
                let boceto :string = "";
                const resp = await apiCall('GET', `order/${id}/consecutive/${c.id}/boceto/`, null, '', 'Error al obtener el boceto');
                if (resp?.image){
                    boceto = resp.image;
                }
                return {...c, image_cache: boceto};
            } );

            Promise.all(newConsecutives).then((newConsecutives) => {
                // sort by consecutive number
                newConsecutives.sort((a : ConsecutiveType, b : ConsecutiveType) => a.consecutive_number - b.consecutive_number);
                dispatch(setConsecutives(newConsecutives));
                resolve(true);
            }).catch((error) => {
                console.log(error);
                reject(false);
            } );
        });

    }

    const handleSelectItem = (e:any, consecutive : ConsecutiveType) => {
        if (e.target.checked){
            setSelectedForPrint([...selectedForPrint, consecutive]);
        } else {
            setSelectedForPrint(selectedForPrint.filter((c : ConsecutiveType) => c.id !== consecutive.id));
        }
    }

    const handleEnablePrint = async (enable : boolean) => {

        await getBocetosForPrint();

        setEnablePrint(enable);

        const columnsToexport = [
            {title : 'Seleccionar', field: 'select', type: 'checkbox' },
            ...columns
        ]

        if (!enable) {
            setSelectedForPrint([]);
            setColumns(initialColumns);
        }else {
            setColumns(columnsToexport);
        }

    }

    const ableToAddConsecutives = order.status !== constants.orderStatus.FINALIZADO && order.status !== constants.orderStatus.CANCELADA && order.status !== constants.orderStatus.EN_PROCESO && canAdd_orderconsecutive;
    const ableToSendToValidation = canValidate_orders || canView_orders_all || canView_orders_own;
    const canSendOrderOnly =
        canAdd_process &&
        !isLegacyLocked &&
        order?.status !== constants.orderStatus.CREADA &&
        (!order?.order_process_id || hasUnsentConsecutives);
    const canSendOrderToValidation = ableToSendToValidation && order?.status === constants.orderStatus.CREADA;

    return <div className="container-wrap">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3} className="text-left">
                        <h2 className="text-left">Consecutivos de Orden No. {id}</h2>
                    </Grid>

                    { !enablePrint ? <>
                        <Grid item xs={12} sm={3} className="text-right">
                            {canSendOrderOnly ? (
                                <Button
                                    className="btn btn-primary"
                                    onClick={() => handleSendOrderToProduction(!order?.order_process_id)}
                                >
                                    {order?.order_process_id && hasUnsentConsecutives
                                        ? 'Enviar Consecutivos a Producción'
                                        : 'Enviar Orden a Producción'}
                                </Button>
                            ) : null}
                            {canSendOrderToValidation ? (
                                <Button className="btn btn-primary" onClick={handleSendOrderToValidation}>
                                    Enviar Orden a Validación
                                </Button>
                            ) : null}
                        </Grid>
                        <Grid item xs={12} sm={1} className="text-right"></Grid>
                        <Grid item xs={12} sm={1} className="d-flex-center icon-btn">
                            {canView_orderconsecutive && (
                              <FontAwesomeIcon icon={faPrint} size="2x" className="text-primary" onClick={()=>handleEnablePrint(true)} />
                            )}
                        </Grid>
                        </>
                        :
                    <>
                        <Grid item xs={12} sm={1} className="text-center">
                            {canView_orderconsecutive && <Button className="btn btn-primary" onClick={()=>handleEnablePrint(false)}>Cancelar</Button>}
                        </Grid>
                        <Grid item xs={12} sm={2} className="text-right">
                            {canView_orderconsecutive && (
                              selectedForPrint.length < consecutives.length
                                ? <Button className="btn btn-primary" onClick={handlePrintSelecteAll}>Seleccionar Todos</Button>
                                : <Button className="btn btn-primary" onClick={handlePrintSelecteNone}>Deseleccionar Todos</Button>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={2} className="text-right">
                            {canView_orderconsecutive && selectedForPrint.length > 0 ?<Button className="btn btn-primary" onClick={handlePrintConsecutiveBatch}>Imprimir</Button>  : null }
                        </Grid>
                    </>
                    }
                    <Grid item xs={12} sm={2} className="text-right">
                    {canView_orders && <Button className="btn btn-primary" onClick={() => navigate('/orden/' + id)}>Regresar a la orden</Button>}
                    </Grid>
                    <Grid item xs={12} sm={2} className="text-right">
                        {canAdd_orderconsecutive && <Button title={!ableToAddConsecutives ? "Esta orden ya no puede modificarse":"Agregar nuevo consecutivo"} className="btn btn-primary" onClick={() => navigate('/orden/' + id + '/consecutivo/crear')} disabled={!ableToAddConsecutives}>Agregar</Button>}
                    </Grid>
                </Grid>
                <DataTable data={consecutivesWithFlags} columns={columns} actions={actions} filterByDateBy={filterDate} selectItem={handleSelectItem}  itemsSelected={selectedForPrint} />
            </div>;
}

    export default ViewConsecutives;
